import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { DailyMetricRow } from '@monitoring-panel/types';
import { formatCount, formatInt, formatPercent, formatShortDate } from '@/lib/report-format';
import { formatDecimal } from '@/lib/utils';
import {
  ADVIA_CHART_OPTS,
  ADVIA_CHART_TOKENS,
  ADVIA_ECHARTS_THEME,
  CHART_SERIES,
  darkStop,
  solid,
  tooltipRow,
  tooltipTitle,
} from '@/lib/advia-charts';

interface ReportTrendChartProps {
  daily: DailyMetricRow[];
}

interface SeriesDef {
  key: string;
  name: string;
  color: string;
  /** Darker stop of the same series, for axis names and labels. */
  textColor: string;
  type: 'bar' | 'line';
  /** Rate series are formatted as % on their axis; counts use compact numbers. */
  isRate: boolean;
  /** Default visibility. */
  on: boolean;
  value: (d: DailyMetricRow) => number;
  format: (v: number) => string;
}

/**
 * Series catalog. Every active series gets its OWN y-axis with an independent
 * scale, so a 86M "measurable" series never flattens a 147 "clicks" series.
 * Volume metrics render as bars, rates/engagement as lines.
 */
const SERIES: SeriesDef[] = [
  { key: 'impressions', name: 'Impressions', color: solid(CHART_SERIES.graphite), textColor: darkStop(CHART_SERIES.graphite), type: 'bar', isRate: false, on: true, value: (d) => d.impressions_count, format: formatCount },
  { key: 'measurable', name: 'Measurable impr.', color: solid(CHART_SERIES.sky), textColor: darkStop(CHART_SERIES.sky), type: 'bar', isRate: false, on: false, value: (d) => d.measurable_impressions_sum, format: formatCount },
  { key: 'viewable', name: 'Viewable impr.', color: solid(CHART_SERIES.emerald), textColor: darkStop(CHART_SERIES.emerald), type: 'bar', isRate: false, on: false, value: (d) => d.viewable_impressions_sum, format: formatCount },
  { key: 'clicks', name: 'Clicks', color: solid(CHART_SERIES.blue), textColor: darkStop(CHART_SERIES.blue), type: 'bar', isRate: false, on: true, value: (d) => d.clicks_count, format: formatInt },
  { key: 'conversions', name: 'Conversions', color: solid(CHART_SERIES.pink), textColor: darkStop(CHART_SERIES.pink), type: 'bar', isRate: false, on: false, value: (d) => d.total_conversions, format: formatInt },
  { key: 'ctr', name: 'CTR', color: solid(CHART_SERIES.amber), textColor: darkStop(CHART_SERIES.amber), type: 'line', isRate: true, on: true, value: (d) => d.ctr, format: (v) => formatPercent(v) },
  { key: 'viewability', name: 'Viewability', color: solid(CHART_SERIES.violet), textColor: darkStop(CHART_SERIES.violet), type: 'line', isRate: true, on: true, value: (d) => d.viewability_rate, format: (v) => formatPercent(v) },
  { key: 'measurable_rate', name: 'Measurable rate', color: solid(CHART_SERIES.cyan), textColor: darkStop(CHART_SERIES.cyan), type: 'line', isRate: true, on: false, value: (d) => d.measurable_rate, format: (v) => formatPercent(v) },
  { key: 'conversion_rate', name: 'Conversion rate', color: solid(CHART_SERIES.fuchsia), textColor: darkStop(CHART_SERIES.fuchsia), type: 'line', isRate: true, on: false, value: (d) => d.conversion_rate, format: (v) => formatPercent(v) },
];

/** Horizontal room reserved per stacked axis (axis line + labels). */
const AXIS_WIDTH = 58;

/**
 * Daily delivery trend from `daily_metrics`. Series are toggleable and each
 * active series gets its own independent y-axis (alternating left/right), so
 * metrics of wildly different magnitudes stay individually readable.
 */
export function ReportTrendChart({ daily }: ReportTrendChartProps) {
  const [active, setActive] = useState<Set<string>>(
    () => new Set(SERIES.filter((s) => s.on).map((s) => s.key))
  );

  const toggle = (key: string) =>
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const option = useMemo<EChartsOption>(() => {
    const dates = daily.map((d) => d.date);
    const activeSeries = SERIES.filter((s) => active.has(s.key));

    // One axis per active series. Alternate sides (left, right, left, …) and
    // offset each additional axis on the same side so none overlap.
    let leftCount = 0;
    let rightCount = 0;
    const sideOf: Array<'left' | 'right'> = activeSeries.map((_, i) =>
      i % 2 === 0 ? 'left' : 'right'
    );

    const yAxis = activeSeries.map((s, i) => {
      const position = sideOf[i];
      const offset = position === 'left' ? leftCount++ * AXIS_WIDTH : rightCount++ * AXIS_WIDTH;
      return {
        type: 'value' as const,
        name: s.name,
        nameTextStyle: { color: s.textColor },
        position,
        offset,
        // Gridlines only from the first axis to avoid a cluttered background.
        splitLine: { show: i === 0 },
        axisLine: { show: true, lineStyle: { color: s.color } },
        axisLabel: {
          color: s.textColor,
          formatter: (value: number) =>
            s.isRate ? `${formatDecimal(value * 100, 0)}%` : formatCount(value),
        },
      };
    });

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params: unknown) => {
          const items = params as Array<{ axisValue: string; seriesName: string; value: number; marker: string }>;
          if (!items.length) return '';
          const lines = items.map((item) => {
            const def = SERIES.find((s) => s.name === item.seriesName);
            const value = def ? def.format(item.value) : String(item.value);
            return tooltipRow(item.seriesName, value, item.marker);
          });
          return tooltipTitle(items[0].axisValue) + lines.join('');
        },
      },
      legend: { show: false },
      grid: {
        left: Math.max(20, leftCount * AXIS_WIDTH),
        right: Math.max(20, rightCount * AXIS_WIDTH),
        top: 24,
        bottom: 30,
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: { formatter: (value: string) => formatShortDate(value) },
      },
      yAxis,
      series: activeSeries.map((s, i) => ({
        name: s.name,
        type: s.type,
        yAxisIndex: i,
        smooth: s.type === 'line',
        showSymbol: s.type === 'line',
        itemStyle: { color: s.color },
        ...(s.type === 'line' ? { lineStyle: { color: s.color, width: 1.5 } } : {}),
        data: daily.map((d) => s.value(d)),
      })),
    };
  }, [daily, active]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {SERIES.map((s) => {
          const isOn = active.has(s.key);
          return (
            <button
              key={s.key}
              onClick={() => toggle(s.key)}
              aria-pressed={isOn}
              className={`adv-chip gap-2 ${isOn ? 'adv-chip--on' : ''}`}
            >
              <span
                className={`w-2 h-2 rounded-full ${isOn ? 'ring-1 ring-adv-ivory' : ''}`}
                style={{ backgroundColor: isOn ? s.color : ADVIA_CHART_TOKENS.neutralLight }}
              />
              {s.name}
            </button>
          );
        })}
      </div>
      <ReactECharts
        option={option}
        style={{ height: 400 }}
        notMerge
        theme={ADVIA_ECHARTS_THEME}
        opts={ADVIA_CHART_OPTS}
      />
    </div>
  );
}
